/* Verqo Trade Score · Methodology & Disclosures (Iter 160 · Layer 6 + 7).
 *
 * Public, no-auth, audit-ready document. Linked from:
 *   • Buyer signup consent checkbox
 *   • Every TradeScoreBadge tooltip
 *   • My Trade Score panel inside Buyer Settings
 *
 * Plain-English, descriptive-only language. NO probabilistic claims. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, ArrowLeft } from "lucide-react";
import api from "../../lib/api";

export default function TradeScoreMethodology() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/trade-score/methodology").then((r) => setM(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-trade-score">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to Verqo
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <ScrollText className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · Verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Verqo Trade Score — Methodology &amp; Disclosures</h1>
        <div className="text-[12px] text-foreground/55 mt-2">
          {m ? <>Methodology {m.version} · Beta · Effective through {String(m.beta_until || "").slice(0,10)}</> : "Methodology v1.0 · Beta"}
        </div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <h2 className="display text-xl mt-2 mb-2">What the Verqo Trade Score is</h2>
          <p>
            The Verqo Trade Score is an <strong>informational signal</strong>{" "}
            sellers see when reviewing a buyer&apos;s account on Verqo. It is a
            descriptive summary of how the buyer has behaved on the platform —
            how many invoices they have paid, what percentage were paid on or
            before the due date, the average number of days they have paid late,
            and the size of their current outstanding balance.
          </p>
          <p>
            <strong>The Trade Score is currently in Beta.</strong> While in
            Beta, no party should make a final credit decision on the basis of
            the score alone. Sellers must perform their own diligence.
          </p>

          <h2 className="display text-xl mt-8 mb-2">What the Trade Score is NOT</h2>
          <ul>
            <li><strong>Not a credit decision.</strong> Verqo does not approve, decline, set terms for, or extend credit on the basis of the Trade Score. Those decisions are made by the seller, lender, or insurer.</li>
            <li><strong>Not a Consumer Report.</strong> Verqo is a B2B trade-data service and is not a &quot;Consumer Reporting Agency&quot; under the U.S. Fair Credit Reporting Act (FCRA).</li>
            <li><strong>Not a probability of default.</strong> We do not publish or claim that any score implies a particular probability of late payment, default, fraud, or any other future event. The score is descriptive of past on-platform behavior only.</li>
            <li><strong>Not a recommendation.</strong> Verqo does not recommend that any seller extend, refuse, or modify trade credit to any buyer.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">What inputs we use</h2>
          {m?.engine_overview && (
            <p className="text-[12.5px] text-foreground/70 mb-3">{m.engine_overview}</p>
          )}

          {m?.maturity_bands && m.maturity_bands.length > 0 && (
            <>
              <h3 className="display text-base mt-5 mb-2">Maturity bands</h3>
              <ul data-testid="ts-maturity-bands">
                {m.maturity_bands.map((b, idx) => (
                  <li key={idx}>
                    <strong>{b.id.replace("_", " ")}</strong> ({b.criteria}) — {b.weights}. {b.desc}
                  </li>
                ))}
              </ul>
            </>
          )}

          {m?.external_track && m.external_track.length > 0 && (
            <>
              <h3 className="display text-base mt-5 mb-2">External track sources</h3>
              <ul data-testid="ts-external-track">
                {m.external_track.map((i, idx) => (
                  <li key={idx}><strong>{i.name}.</strong> {i.desc}</li>
                ))}
              </ul>
            </>
          )}

          {m?.behavioral_track && m.behavioral_track.length > 0 && (
            <>
              <h3 className="display text-base mt-5 mb-2">Behavioral track sources</h3>
              <ul data-testid="ts-behavioral-track">
                {m.behavioral_track.map((i, idx) => (
                  <li key={idx}><strong>{i.name}.</strong> {i.desc}</li>
                ))}
              </ul>
            </>
          )}

          {!m?.external_track && m?.inputs && (
            <ul data-testid="ts-inputs">
              {m.inputs.map((i, idx) => (
                <li key={idx}><strong>{i.name}.</strong> {i.desc}</li>
              ))}
            </ul>
          )}

          <h2 className="display text-xl mt-8 mb-2">What inputs we DO NOT use</h2>
          {m?.what_we_do_not_use && (
            <ul data-testid="ts-excluded">
              {m.what_we_do_not_use.map((s, idx) => <li key={idx}>{s}</li>)}
            </ul>
          )}

          {m?.sole_proprietor_disclosure && (
            <>
              <h2 className="display text-xl mt-8 mb-2">Sole-proprietor FCRA disclosure</h2>
              <p data-testid="ts-sole-prop">{m.sole_proprietor_disclosure}</p>
            </>
          )}

          <h2 className="display text-xl mt-8 mb-2">Your rights</h2>
          {m?.rights && (
            <ul data-testid="ts-rights">
              {m.rights.map((s, idx) => <li key={idx}>{s}</li>)}
            </ul>
          )}

          <h2 className="display text-xl mt-8 mb-2">Disclaimer</h2>
          <p>
            {m?.disclaimer_full ||
              "The Verqo Trade Score is an informational signal computed from public + on-platform data. It is NOT a credit decision and NOT a recommendation to extend or refuse business. Sellers must perform their own diligence. Verqo does not guarantee accuracy, timeliness, or completeness."}
          </p>

          <h2 className="display text-xl mt-8 mb-2">Changelog</h2>
          {m?.changelog && (
            <ul data-testid="ts-changelog">
              {m.changelog.map((c, idx) => (
                <li key={idx}><strong>{c.version}</strong> ({c.date}) — {c.summary}</li>
              ))}
            </ul>
          )}

          <h2 className="display text-xl mt-8 mb-2">Contact</h2>
          <p>
            Trade Score questions:{" "}
            <a href="mailto:trust@verqo.com" className="underline">trust@verqo.com</a>.{" "}
            Privacy:{" "}
            <a href="mailto:privacy@verqo.com" className="underline">privacy@verqo.com</a>.
          </p>

          <p className="text-[12px] text-foreground/55 mt-10">
            <Link to="/legal/privacy" className="underline">Privacy Policy</Link> ·{" "}
            <Link to="/legal/terms" className="underline">Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
